import React from 'react';
import Select from '../ui/Select';
import Input from '../ui/Input';
import { FILTER_TYPES, TIME_WINDOW_UNITS, COUNT_OPS } from '../../data/events';
import { EARNING_TYPE_BY_PROP } from '../../utils/segmentConditions';
import PropertyFilters from './PropertyFilters';
import MultiValueInput from './MultiValueInput';
import styles from './FilterRow.module.css';

const EMPTY_OPTIONS = { events: [], profileFields: [], entityStatus: [], campaigns: [], campaignStatuses: [] };

// These profile properties commonly need "any of a list" targeting (e.g. a
// campaign for three specific cities, or a handful of known PickMyWork user
// ids) — everything else on 'user-property' stays single-value. Source
// included for the same reason — "installed via Meta OR Google Ads" is a
// realistic segment, not an edge case.
const MULTI_VALUE_PROPS = new Set(['City', 'State', 'User ID', 'Source']);

function TimeWindowInput({ value, unit, onChange }) {
  return (
    <div className={styles.countGroup}>
      <Input
        type="number"
        size="sm"
        min="1"
        style={{ width: 44, textAlign: 'center' }}
        value={value || ''}
        onChange={(e) => onChange({ timeWindowValue: e.target.value })}
      />
      <Select
        size="sm"
        style={{ width: 90 }}
        value={unit || 'days'}
        options={TIME_WINDOW_UNITS.map((u) => ({ value: u, label: u }))}
        onChange={(val) => onChange({ timeWindowUnit: val })}
      />
    </div>
  );
}

class FilterRow extends React.Component {
  render() {
    const { filter, onRemove, onUpdate, showConnector, op, onOpChange } = this.props;
    const options = this.props.options || EMPTY_OPTIONS;

    const isLead     = filter.type === 'lead';
    const isEvent    = filter.type === 'event' || isLead;
    const isCampaign = filter.type === 'campaign';
    const isProperty = ['user-property', 'entity-status', 'earning'].includes(filter.type);
    const isMultiValueProp = filter.type === 'user-property' && MULTI_VALUE_PROPS.has(filter.prop);

    const did = filter.did !== false;
    let ft  = isEvent
      ? (did ? FILTER_TYPES['event-performed'] : FILTER_TYPES['event-not-performed'])
      : (FILTER_TYPES[filter.type] || {});

    // Profile/entity-status property names and their possible values are
    // discovered live from ClickHouse (see segmentService.getOptions) rather
    // than hardcoded, so they're merged in here instead of living in FILTER_TYPES.
    if (filter.type === 'user-property') {
      ft = { ...ft, props: options.profileFields.map((f) => f.label), valsByProp: Object.fromEntries(options.profileFields.map((f) => [f.label, f.values])) };
    } else if (filter.type === 'entity-status') {
      ft = { ...ft, props: options.entityStatus.map((s) => s.label), valsByProp: Object.fromEntries(options.entityStatus.map((s) => [s.label, s.values])) };
    }

    // Event names can appear more than once across the merged tables in
    // theory — dedupe by name, keeping the first table seen. The Lead filter
    // is the same underlying 'event' condition machinery, just scoped to
    // lead_events only — a dedicated button/list rather than making admins
    // find "Lead Verified" buried in the generic (events + earning_events +
    // task_events) Event dropdown.
    const events = [];
    const seenEvents = new Set();
    for (const ev of options.events) {
      if (isLead && ev.table !== 'lead_events') continue;
      if (seenEvents.has(ev.name)) continue;
      seenEvents.add(ev.name);
      events.push(ev);
    }

    const upd    = (patch) => onUpdate(filter.id, patch);
    const vals   = ft.valsByProp ? ft.valsByProp[filter.prop] : null;
    const propFilters = filter.propFilters || [];
    const updPfs = (pfs) => upd({ propFilters: pfs });
    const isEarning = filter.type === 'earning';
    const rawEarningType = isEarning ? EARNING_TYPE_BY_PROP[filter.prop] : undefined;
    // Property-sample discovery (segments.controller.js's /event-properties)
    // only accepts a single entity_type — grouped options ('Project' =
    // ['JOB','JOB_TASK']) pass just the first as a representative sample
    // source rather than needing the backend to also support arrays here,
    // since this is only an autocomplete suggestion, not the actual filter.
    const earningEntityType = Array.isArray(rawEarningType) ? rawEarningType[0] : rawEarningType;

    return (
      <div className={styles.wrap}>
        {showConnector && (
          <div className={styles.connector}>
            <button className={[styles.opBtn, op === 'AND' ? styles.on : ''].join(' ')} onClick={() => onOpChange('AND')}>AND</button>
            <button className={[styles.opBtn, op === 'OR' ? styles.on : ''].join(' ')} onClick={() => onOpChange('OR')}>OR</button>
          </div>
        )}

        <div
          className={[styles.row, isEvent ? styles.rowEvent : ''].join(' ')}
          style={{ borderLeft: '3px solid ' + ft.tc }}
        >

          {/* ─── Event filter ──────────────────────────────── */}
          {isEvent && (
            <div className={styles.eventBlock}>
            <div className={styles.eventMain}>
              <span className={styles.typeTag} style={{ color: ft.tc, background: ft.col }}>{isLead ? 'Lead' : 'Event'}</span>

              <div className={styles.didToggle}>
                <button
                  className={[styles.didBtn, did ? styles.didOn : ''].join(' ')}
                  onClick={() => upd({ did: true })}
                >Did</button>
                <button
                  className={[styles.didBtn, !did ? styles.didNotOn : ''].join(' ')}
                  onClick={() => upd({ did: false })}
                >Did Not</button>
              </div>

              <Select
                size="sm"
                style={{ width: 200 }}
                value={filter.event || ''}
                placeholder={events.length ? (isLead ? 'Select lead event…' : 'Select event…') : 'Loading events…'}
                options={events.map((ev) => ({ value: ev.name, label: ev.name }))}
                onChange={(val) => {
                  const chosen = events.find((ev) => ev.name === val);
                  upd({ event: val, eventTable: chosen?.table || (isLead ? 'lead_events' : 'events'), propFilters: [] });
                }}
              />

              <span className={styles.dimLbl}>in last</span>

              <TimeWindowInput value={filter.timeWindowValue} unit={filter.timeWindowUnit} onChange={upd} />

              {did && (
                <div className={styles.countGroup}>
                  <Select
                    size="sm"
                    style={{ width: 110 }}
                    value={filter.countOp || 'at least'}
                    options={COUNT_OPS.map((c) => ({ value: c, label: c }))}
                    onChange={(val) => upd({ countOp: val })}
                  />
                  <Input
                    type="number"
                    size="sm"
                    min="1"
                    style={{ width: 44, textAlign: 'center' }}
                    value={filter.countVal || '1'}
                    onChange={(e) => upd({ countVal: e.target.value })}
                  />
                  <span className={styles.dimLbl}>×</span>
                </div>
              )}

              <button className={styles.removeBtn} onClick={() => onRemove(filter.id)}>
                <span className="material-symbols-rounded" style={{ fontSize: 13 }}>close</span>
              </button>
            </div>

              <PropertyFilters
                propFilters={propFilters}
                onChange={updPfs}
                eventName={filter.event}
                eventTable={filter.eventTable}
                idPrefix={filter.id}
              />
            </div>
          )}

          {/* ─── Campaign filter — pick a specific past campaign + outcome ── */}
          {isCampaign && (
            <>
              <span className={styles.typeTag} style={{ color: ft.tc, background: ft.col }}>{ft.label}</span>

              <div className={styles.didToggle}>
                <button
                  className={[styles.didBtn, did ? styles.didOn : ''].join(' ')}
                  onClick={() => upd({ did: true })}
                >Was</button>
                <button
                  className={[styles.didBtn, !did ? styles.didNotOn : ''].join(' ')}
                  onClick={() => upd({ did: false })}
                >Was Not</button>
              </div>

              <Select
                size="sm"
                style={{ width: 160 }}
                value={filter.status || ''}
                placeholder="Select outcome…"
                options={options.campaignStatuses.map((s) => ({ value: s.status, label: s.label }))}
                onChange={(val) => upd({ status: val })}
              />

              <span className={styles.dimLbl}>in</span>

              <Select
                size="sm"
                style={{ width: 200 }}
                value={filter.campaignId || ''}
                placeholder={options.campaigns.length ? 'Select campaign…' : 'Loading campaigns…'}
                options={options.campaigns.map((c) => ({ value: c.id, label: c.name }))}
                onChange={(val) => upd({ campaignId: val })}
              />

              <button className={styles.removeBtn} onClick={() => onRemove(filter.id)}>
                <span className="material-symbols-rounded" style={{ fontSize: 13 }}>close</span>
              </button>
            </>
          )}

          {/* ─── Profile / entity-status / earning ── */}
          {isProperty && ft.props && (
            <>
              <span className={styles.typeTag} style={{ color: ft.tc, background: ft.col }}>{ft.label}</span>
              <Select
                size="sm"
                style={{ width: 170 }}
                value={filter.prop || ''}
                placeholder="Select property…"
                options={ft.props.map((p) => ({ value: p, label: p }))}
                onChange={(val) => upd({ prop: val, val: '' })}
              />
              {ft.ops && (
                <Select
                  size="sm"
                  style={{ width: 130 }}
                  value={filter.op || ft.ops[0]}
                  options={ft.ops.map((o) => ({ value: o, label: o }))}
                  onChange={(val) => upd({ op: val })}
                />
              )}
              {!ft.noValue && (
                isMultiValueProp
                  ? (
                    <MultiValueInput
                      options={vals}
                      value={Array.isArray(filter.val) ? filter.val : (filter.val ? [filter.val] : [])}
                      onChange={(arr) => upd({ val: arr })}
                      placeholder={vals ? 'Select or type…' : 'Type an id, press Enter…'}
                    />
                  )
                  : vals
                    ? (
                      <Select
                        size="sm"
                        style={{ width: 170 }}
                        value={filter.val || ''}
                        placeholder="Select…"
                        options={vals.map((v) => ({ value: v, label: v }))}
                        onChange={(v) => upd({ val: v })}
                      />
                    )
                    : (
                      <Input
                        size="sm"
                        style={{ width: 170 }}
                        type={ft.numericValue ? 'number' : 'text'}
                        placeholder="value…"
                        value={filter.val || ''}
                        onChange={(e) => upd({ val: e.target.value })}
                      />
                    )
              )}
              {ft.needsTimeWindow && (
                <>
                  <span className={styles.dimLbl}>in last</span>
                  <TimeWindowInput value={filter.timeWindowValue} unit={filter.timeWindowUnit} onChange={upd} />
                </>
              )}
              <button className={styles.removeBtn} onClick={() => onRemove(filter.id)}>
                <span className="material-symbols-rounded" style={{ fontSize: 13 }}>close</span>
              </button>
              {isEarning && (
                <PropertyFilters
                  propFilters={propFilters}
                  onChange={updPfs}
                  entityType={earningEntityType}
                  eventTable="earning_events"
                  idPrefix={filter.id}
                />
              )}
            </>
          )}
        </div>
      </div>
    );
  }
}

export default FilterRow;
